import { useCallback, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { ConnectorPromoConfig, PromoDismissed } from '../connector-promo-config-types.ts';
import { getDaysSinceEnrolment } from '../utils/enrolmentWindow.ts';
import { getRecommendedConnectors } from '../utils/getRecommendedConnectors.ts';

import { useConnectorOnboardingTeamType } from './useConnectorOnboardingTeamType.ts';
import { useConnectorStates } from './useConnectorStates.ts';
import { useSetupConnector } from './useSetupConnector.ts';

/**
 * Computes the next PromoDismissed value when a surface is dismissed.
 * Merges with the existing state so sidebar and banner can be dismissed independently.
 */
function getNextPromoDismissed(
	current: PromoDismissed | undefined,
	surface: 'sidebar' | 'banner',
): PromoDismissed {
	if (current === undefined || current === surface) {
		return surface;
	}
	return 'both';
}

const DISABLED_CONFIG: ConnectorPromoConfig = {
	recommendedConnectors: [],
	spotlightShown: false,
	enrolmentDate: null,
	shouldShowEmptyState: false,
	shouldShowNthPromo: false,
	shouldShowNthPagePromo: false,
	shouldShowPostConnectPromo: false,
	shouldShowPostConnectPagePromo: false,
	shouldShowSidebarItem: false,
	shouldShowSpotlight: false,
	onSetupConnector: () => {},
	onDismissPromo: () => {},
	onMaybeLater: () => {},
	onDismissSpotlight: () => {},
	activeConnectorKey: null,
	closeModal: () => {},
};

export function useConnectorPromoConfig({
	hasRecentItems = false,
}: {
	hasRecentItems?: boolean;
} = {}): ConnectorPromoConfig {
	// No experiment check here. Surface components gate rendering via
	// useIsConnectorOnboardingEnabled() before calling this hook.
	const { teamType } = useConnectorOnboardingTeamType({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	});
	const {
		connectorStates,
		connectedCount,
		loading: connectorStatesLoading,
		error: connectorStatesError,
	} = useConnectorStates({ skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg') });

	const { openSetupModal, activeConnectorKey, closeModal } = useSetupConnector();
	const { setOnboardingState } = useSetOnboardingState();

	// Carries the latest promoDismissed value into callbacks without violating rules-of-hooks.
	// Updated on every render after the loading/error early returns.
	const promoDismissedRef = useRef<PromoDismissed | undefined>(undefined);

	// Stable callback refs — must be declared before any early returns to satisfy rules-of-hooks
	const onSetupConnector = useCallback(
		(connectorKey: string) => openSetupModal(connectorKey),
		[openSetupModal],
	);
	const onDismissPromo = useCallback(
		(surface: 'sidebar' | 'banner') => {
			const next = getNextPromoDismissed(promoDismissedRef.current, surface);
			if (next === promoDismissedRef.current) {
				return;
			}
			setOnboardingState({
				key: TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
				value: next,
			});
		},
		[setOnboardingState],
	);
	const onMaybeLater = useCallback(() => {}, []);
	const onDismissSpotlight = useCallback(() => {
		setOnboardingState({
			key: TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN,
			value: 'true',
		});
	}, [setOnboardingState]);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			TWCG_1489_CONNECTOR_ONBOARDING.ENROLMENT_DATE,
			TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN,
			TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
		],
		!fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	);

	if (onboardingStateError) {
		if (isUnauthorizedError(onboardingStateError)) {
			markErrorAsHandled(onboardingStateError);
		}
		return DISABLED_CONFIG;
	}

	if (connectorStatesError) {
		if (isUnauthorizedError(connectorStatesError)) {
			markErrorAsHandled(connectorStatesError);
		}
		return DISABLED_CONFIG;
	}

	if (onboardingStateLoading || connectorStatesLoading) {
		return DISABLED_CONFIG;
	}

	// If 2+ connectors are already configured org-wide the site is not a
	// good onboarding target; hide the entire experiment.
	if (connectedCount >= 2) {
		return DISABLED_CONFIG;
	}

	const { recommendedConnectors, connectedAppName } = getRecommendedConnectors(
		teamType,
		connectorStates,
	);
	const hasConnectedAny = connectedAppName !== undefined;
	const nextRecommendedConnector = recommendedConnectors[0];

	const onboardingState = deserializeState(onboardingStateData);

	const promoDismissed =
		(onboardingState.promoDismissed as PromoDismissed | undefined) ?? undefined;

	// Keep the ref in sync so dismiss callbacks always close over the latest value.
	promoDismissedRef.current = promoDismissed;
	const spotlightShown = !!onboardingState.spotlightShown;

	const enrolmentDateStr = onboardingState.enrolmentDate as string | undefined;
	const enrolmentDate = enrolmentDateStr ? new Date(enrolmentDateStr) : null;
	const daysSinceEnrolment = getDaysSinceEnrolment(enrolmentDateStr);

	const sidebarDismissed = promoDismissed === 'sidebar' || promoDismissed === 'both';
	const bannerDismissed = promoDismissed === 'banner' || promoDismissed === 'both';

	return {
		recommendedConnectors,
		connectedAppName,
		nextRecommendedConnector,
		spotlightShown,
		enrolmentDate,
		promoDismissed,
		shouldShowEmptyState: !hasConnectedAny && !hasRecentItems,
		shouldShowNthPromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowNthPagePromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPromo: hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPagePromo: hasConnectedAny && !bannerDismissed,
		shouldShowSidebarItem: !sidebarDismissed,
		shouldShowSpotlight: !!enrolmentDate && daysSinceEnrolment >= 3 && !spotlightShown,
		onSetupConnector,
		onDismissPromo,
		onMaybeLater,
		onDismissSpotlight,
		activeConnectorKey,
		closeModal,
	};
}
